import React, { useState, useEffect } from "react";
import { Box, Button, Card, CardContent, Typography, Grid, Link } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import theme from "../../../components/LayoutComponents/theme";
import { getProgramCourses } from "../../../remote/programRequests";
import { getCourseSkills } from "../../../remote/courseRequest";
import LoadingCircle from "../../../components/LayoutComponents/LoadingCircle";
import CoursesCard from "./CoursesCard";
import PurpleButton from "../../../components/LayoutComponents/PurpleButton";

export default function ProgramCard({ session, program, handleClose, courses, jobSkills = [], userSkills = [] }) {
    const [programCourses, setProgramCourses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCourses, setShowCourses] = useState(false);
    const [courseSkills, setCourseSkills] = useState({});
    const [totalPercent, setTotalPercent] = useState(0);

    // Fetch program courses and course skills
    useEffect(() => {
        if (!session) return;

        const fetchProgramAndSkills = async () => {
            try {
                setLoading(true);

                // 1. Fetch program courses
                const res = await getProgramCourses(session.token, program.programId);
                const coursesData = res.data || [];
                setProgramCourses(coursesData);

                if (coursesData.length > 0) {
                    // 2. Fetch all course skills in parallel
                    const skillsResults = await Promise.all(
                        coursesData.map(async (course) => {
                            try {
                                const res = await getCourseSkills(session.token, course.courseId);
                                return [course.courseId, res.data || []];
                            } catch (err) {
                                console.error(`Error fetching skills for ${course.courseId}`, err);
                                return [course.courseId, []];
                            }
                        })
                    );

                    setCourseSkills(Object.fromEntries(skillsResults));
                }
            } catch (e) {
                console.error("Error fetching program courses:", e);
            } finally {
                setLoading(false);
            }
        };

        fetchProgramAndSkills();
    }, [session, program.programId]);

    // Calculate total program improvement after courseSkills is set
    useEffect(() => {
        if (!programCourses.length || !Object.keys(courseSkills).length) return;

        const allRelevantSkills = programCourses.flatMap((pc) => {
            const skillsForCourse = courseSkills[pc.courseId] || [];
            return skillsForCourse.filter((skill) => {
                const isJobSkill = jobSkills.some(js => js.compId === skill.compId);
                const isUserSkill = userSkills.some(us => us.compId === skill.compId);
                return isJobSkill && !isUserSkill;
            });
        });

        // Remove duplicates
        const uniqueRelevantSkills = Array.from(
            new Map(allRelevantSkills.map((s) => [s.compId, s])).values()
        );

        const percentage = jobSkills.length > 0
            ? ((uniqueRelevantSkills.length / jobSkills.length) * 100).toFixed(1)
            : 0;

        setTotalPercent(percentage);
    }, [courseSkills, programCourses, jobSkills, userSkills]);

    if (loading) {
        return <LoadingCircle />;
    }

    return (
        <Box>
            <Button onClick={handleClose}>
                <ArrowBackIcon sx={{ color: theme.colors.purple }} />
            </Button>

            <Card sx={{ maxWidth: 600, margin: "20px auto", p: 2, boxShadow: 3 }}>
                <CardContent>
                    <Typography variant="h5" fontWeight="bold" gutterBottom>
                        {program.programName}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ color: theme.colors.purple, mb: 2 }}>
                        Total Program Improvement: {totalPercent}%
                    </Typography>

                    <Grid container spacing={1}>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">Career Cluster:</Typography>
                            <Typography>{program.careerClusterName}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">Credit Hours:</Typography>
                            <Typography>{program.creditHours}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">Degree Type:</Typography>
                            <Typography>{program.degreeTypeName}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">Delivery Format:</Typography>
                            <Typography>{program.deliveryFormatName}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">School:</Typography>
                            <Typography>{program.entityName}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">Effective Start:</Typography>
                            <Typography>{program.effectiveStartDate ? new Date(program.effectiveStartDate).toLocaleDateString() : "N/A"}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">Effective End:</Typography>
                            <Typography>{program.effectiveEndDate ? new Date(program.effectiveEndDate).toLocaleDateString() : "N/A"}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">Active:</Typography>
                            <Typography>{program.isActiveFlag ? "Yes" : "No"}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="subtitle2">Featured:</Typography>
                            <Typography>{program.isFeatured ? "Yes" : "No"}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2">Additional Info:</Typography>
                            <Typography>{program.additionalInformation || "N/A"}</Typography>
                        </Grid>
                        {program.linkAdditionalInformation && (
                            <Grid item xs={12}>
                                <Typography variant="subtitle2">Link:</Typography>
                                <Link href={program.linkAdditionalInformation} target="_blank">
                                    {program.linkAdditionalInformation}
                                </Link>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2">Program Description:</Typography>
                            <Typography>{program.programDescription || "N/A"}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="subtitle2">Program Requirements:</Typography>
                            <Typography>{program.programRequirements || "N/A"}</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <PurpleButton onClick={() => setShowCourses(true)} label="View Courses" />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {showCourses && (
                <CoursesCard
                    session={session}
                    open={showCourses}
                    onClose={() => setShowCourses(false)}
                    programCourses={programCourses}
                    courses={courses}
                    programName={program.programName}
                    courseSkills={courseSkills}
                    jobSkills={jobSkills}
                    userSkills={userSkills}
                />
            )}
        </Box>
    );
}
